import apiClient from "../utils/apiClient";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { distputTypeSchema } from "../validation";
import Swal from "sweetalert2";
import { useMyContext } from "../context";
import useFilter from "./useFilter";
import { useEffect, useState, useCallback, useRef } from "react";
import * as signalR from "@microsoft/signalr";
import { getRealtimeBaseUrl } from "../utils/realtimeBaseUrl";
import { getAcceptLanguage } from "../utils/acceptLanguage";

const useSupport = ({ disputId, supportId, page } = {}) => {
  const {
    showErrorAlert,
    handleCloseModal,
    id,
    setId,
    selectedSupportType,
    setSelectedSupportType,
    quickData,
    setQuickAction,
    decisionData,
    setDecisionData,
    quickDataSupport,
    setNote,
    note,
    tab,
    adminId,
    setQuickDataSupport,
  } = useMyContext();
  const queryClient = useQueryClient();
  const { type_id, user_id, status, date_from, date_to } = useFilter();
  const hasConnectedRef = useRef(false);
  // token للاستخدام في SignalR فقط (realtime host)
  const getToken = () => sessionStorage.getItem("ecoToken");

  // إضافة حالة SignalR
  const connectionRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const disputeRef = useRef(null);

  useEffect(() => {
    if (supportId) {
      setNote((prev) => ({
        ...prev,
        ticket_id: supportId,
      }));
    }
  }, [supportId, setNote]);

  const disconnectFromSignalR = useCallback(async () => {
    const conn = connectionRef.current;

    if (!conn) return;

    try {
      await conn.stop();
      console.log("🔌 Disconnected from SignalR");
    } catch (err) {
      console.error("❌ Disconnect error:", err);
    } finally {
      connectionRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const joinChatRoom = async (chatId) => {
    const conn = connectionRef.current;

    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      throw new Error("No active connection");
    }

    await conn.invoke("JoinChat", chatId);

    console.log("✅ Joined:", chatId);
  };

  const leaveChatRoom = async (chatId) => {
    const conn = connectionRef.current;

    if (!conn || conn.state !== signalR.HubConnectionState.Connected) return;

    await conn.invoke("LeaveChat", chatId);
  };

  const sendSignalRMessage = async (chatId, message, senderType = "admin") => {
    const conn = connectionRef.current;

    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      throw new Error("No active connection");
    }

    return conn.invoke("SendMessage", {
      chatId,
      content: message,
      senderType,
      timestamp: new Date().toISOString(),
    });
  };
  // باقي الكود الأصلي...
  const {
    data: distputTypes,
    error: distputTypesError,
    isError: distputTypesIsError,
    isLoading: distputTypesLoading,
  } = useQuery({
    queryKey: ["distputTypes"],
    queryFn: async () => {
      const res = await apiClient.get("/dispute-types");
      return res.data;
    },
  });

  //   add edit distput type
  const {
    register,
    formState: { errors },
    control,
    reset,
    handleSubmit,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(distputTypeSchema),
    defaultValues: {
      name_ar: "",
      name_en: "",
      is_active: true,
    },
  });

  // Combined create/update mutation
  const addUpdateDistputType = useMutation({
    mutationFn: async (data) => {
      const payload = {
        name_ar: data?.name_ar,
        name_en: data?.name_en,
        is_active: data?.is_active,
      };

      // If we have an ID, it's an update (PUT)
      if (id) {
        const res = await apiClient.post(`/dispute-types/${id}`, {
          ...payload,
          _method: "PUT",
        });
        return res.data;
      }
      // Otherwise it's a create (POST)
      else {
        const res = await apiClient.post("/dispute-types", payload);
        return res.data;
      }
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: id ? "Type updated successfully" : "Type created successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["distputTypes"] });
      handleCloseModal("addEditDistput");
      reset(); // Reset form after success
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const addUpdateDistputTypeLoading = addUpdateDistputType.isLoading;

  const handleAddUpdateDistputType = handleSubmit((data) => {
    addUpdateDistputType.mutate(data);
  });

  //   get type by id
  const distputTypeById = id
    ? distputTypes?.data.find((e) => e.id === id)
    : null;
  // delete type
  const deleteDistputType = useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete(`/dispute-types/${id}`);
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Type deleted successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["distputTypes"] });
      handleCloseModal("delete");
      setId("");
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const deleteTypeLoading = deleteDistputType.isLoading;
  const handleDeleteType = () => {
    deleteDistputType.mutate();
  };

  const [supportTypesPage, setSupportTypesPage] = useState(1);

  const buildSupportCategoryFormData = (data, categoryId) => {
    const formData = new FormData();
    formData.append("name_ar", data?.name_ar ?? "");
    formData.append("name_en", data?.name_en ?? "");
    formData.append("is_active", data?.is_active ? "1" : "0");

    if (categoryId) {
      formData.append("_method", "PUT");
    }

    return formData;
  };

  const {
    data: supportTypes,
    error: supportTypesError,
    isError: supportTypesIsError,
    isLoading: supportTypesLoading,
  } = useQuery({
    queryKey: ["supportTypes", supportTypesPage],
    queryFn: async () => {
      const res = await apiClient.get(
        `/technical-support-categories?page=${supportTypesPage}`,
      );
      return res.data;
    },
  });

  const {
    register: registerSupportType,
    formState: { errors: supportTypeErrors },
    control: supportTypeControl,
    reset: resetSupportType,
    handleSubmit: handleSupportTypeSubmit,
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(distputTypeSchema),
    defaultValues: {
      name_ar: "",
      name_en: "",
      is_active: true,
    },
  });

  const addUpdateSupportType = useMutation({
    mutationFn: async (data) => {
      const formData = buildSupportCategoryFormData(data, id);

      if (id) {
        const res = await apiClient.post(
          `/technical-support-categories/${id}`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          },
        );
        return res.data;
      }

      const res = await apiClient.post(
        "/technical-support-categories",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: id
          ? "Support type updated successfully"
          : "Support type created successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["supportTypes"] });
      handleCloseModal("addEditSupportType");
      resetSupportType();
      setSelectedSupportType(null);
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const addUpdateSupportTypeLoading = addUpdateSupportType.isLoading;

  const handleAddUpdateSupportType = handleSupportTypeSubmit((data) => {
    addUpdateSupportType.mutate(data);
  });

  const supportTypeById = id
    ? selectedSupportType?.id === id
      ? selectedSupportType
      : supportTypes?.data?.list?.find((item) => item.id === id)
    : null;

  const deleteSupportType = useMutation({
    mutationFn: async () => {
      const res = await apiClient.delete(`/technical-support-categories/${id}`);
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Support type deleted successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["supportTypes"] });
      handleCloseModal("delete");
      setId("");
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const deleteSupportTypeLoading = deleteSupportType.isLoading;
  const handleDeleteSupportType = () => {
    deleteSupportType.mutate();
  };

  //   get distputs with conditional parameters
  const {
    data: distputs,
    error: distputsError,
    isError: distputsIsError,
    isLoading: distputsLoading,
  } = useQuery({
    queryKey: ["distputs", status, type_id, user_id, date_from, date_to],
    queryFn: async () => {
      // Build query parameters object
      const params = new URLSearchParams();

      // Only add parameters if they have values
      if (status) params.append("status", status);
      if (type_id) params.append("type_id", type_id);
      if (user_id) params.append("user_id", user_id);
      if (date_from) params.append("from_date", date_from);
      if (date_to) params.append("to_date", date_to);

      const queryString = params.toString();
      const url = `/disputes/filter${queryString ? `?${queryString}` : ""}`;

      const res = await apiClient.get(url);
      return res.data;
    },
  });

  //   reject dispute
  const rejectDistPut = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(
        `/disputes/reject/${id}`,
        {}, // Empty body
      );
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Dispute rejected successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["distputs"] });
      handleCloseModal("confirm");
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const rejectDistPutLoading = rejectDistPut.isLoading;
  const handleRejectDistput = () => {
    rejectDistPut.mutate();
  };

  //   approve dispute
  const approveDistput = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(
        `/disputes/approve/${id}`,
        {}, // Empty body
      );
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Dispute approved successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["distputs"] });
      handleCloseModal("confirm");
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const approveDistPutLoading = approveDistput.isLoading;
  const handleApproveDistput = () => {
    approveDistput.mutate();
  };

  // get disput by id
  const {
    data: disputDetails,
    error: disputDetailsError,
    isError: disputDetailsIsError,
    isLoading: disputDetailsLoading,
    refetch: refetchDisputDetails,
  } = useQuery({
    queryKey: ["disputById", disputId],
    queryFn: async () => {
      const res = await apiClient.get(`/disputes/${disputId}`);
      return res.data;
    },
    enabled: !!disputId,
  });
  useEffect(() => {
    disputeRef.current = disputDetails?.data;
  }, [disputDetails]);
  const createSignalRConnection = useCallback(() => {
    const token = getToken();
    if (!token) return null;

    const signalRUrl = getRealtimeBaseUrl() || window.location.origin;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${signalRUrl}/hubs/chat`, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Information)
      .build();

    /* ================= Events ================= */

    conn.on("ReceiveMessage", (data) => {
      console.log("📩 New message:", data);

      const event = new CustomEvent("chat-message", {
        detail: { chatId: data.chatId, message: data },
      });

      window.dispatchEvent(event);

      if (conn.state === signalR.HubConnectionState.Connected) {
        setTimeout(() => {
          conn.invoke("ConfirmDelivered", data.id).catch(console.error);
          conn.invoke("ConfirmRead", data.id).catch(console.error);
        }, 500);
      }
    });

    conn.onclose(() => {
      console.log("🔌 Connection closed");
      setIsConnected(false);
    });

    conn.onreconnecting(() => {
      console.log("🔄 Reconnecting...");
      setIsConnected(false);
    });

    conn.onreconnected(() => {
      console.log("✅ Reconnected");

      setIsConnected(true);

      setTimeout(async () => {
        const dispute = disputeRef.current;
        if (!dispute) return;

        try {
          if (dispute.chat_id) {
            await joinChatRoom(dispute.chat_id);
          }

          if (dispute.against_chat_id) {
            await joinChatRoom(dispute.against_chat_id);
          }
        } catch (err) {
          console.error("Rejoin error:", err);
        }
      }, 1000);
    });

    return conn;
  }, []);
  const connectToSignalR = useCallback(async () => {
    const existing = connectionRef.current;

    if (existing) {
      const state = existing.state;

      if (
        state === signalR.HubConnectionState.Connected ||
        state === signalR.HubConnectionState.Connecting
      ) {
        console.log("⚠️ Already connected");
        return true;
      }
    }

    try {
      setIsConnecting(true);

      const conn = createSignalRConnection();
      if (!conn) throw new Error("No token");

      connectionRef.current = conn;

      await conn.start();

      setIsConnected(true);

      console.log("✅ SignalR Connected");

      return true;
    } catch (err) {
      console.error("❌ Connect error:", err);

      connectionRef.current = null;
      setIsConnected(false);

      return false;
    } finally {
      setIsConnecting(false);
    }
  }, [createSignalRConnection]);
  useEffect(() => {
    const token = getToken();

    if (!token) return;
    if (hasConnectedRef.current) return;

    hasConnectedRef.current = true;

    connectToSignalR();

    return () => {
      disconnectFromSignalR();
      hasConnectedRef.current = false;
    };
  }, []);
  // الحصول على معرف الدردشة بناءً على التبويب الحالي
  const getCurrentChatId = () => {
    if (!disputDetails?.data) return null;

    const disputeData = disputDetails.data;
    if (tab === "complainant") {
      return disputeData.chat_id;
    } else if (tab === "against") {
      return disputeData.against_chat_id;
    }
    return null;
  };

  // Get chat messages - محدثة
  const {
    data: chatMessagesData,
    isLoading: chatMessagesLoading,
    refetch: refetchChatMessages,
    error: chatMessagesError,
  } = useQuery({
    queryKey: ["messages", disputId, tab, page],
    queryFn: async () => {
      if (!disputId) return null;

      const chatId = getCurrentChatId();
      if (!chatId) {
        console.log("No chat ID found for current tab");
        return { data: [] };
      }

      try {
        // بناء الرابط بشكل ديناميكي حسب وجود page
        // Realtime host مختلف عن VITE_API_URL، نستخدم axios مباشرة
        let url = `${getRealtimeBaseUrl()}/Chat/${chatId}/messages`;
        if (page !== undefined && page !== null) {
          url += `?page=${page}`;
        }

        // استخدام fetch مباشرة لأن URL مختلف
        const token = sessionStorage.getItem("ecoToken");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const res = { data: await response.json() };
        console.log("Chat messages fetched:", res.data);
        return res.data;
      } catch (error) {
        console.error("Error fetching chat messages:", error);
        if (error.response?.status === 404) {
          return { data: [] };
        }
        throw error;
      }
    },
    enabled: !!disputId && !!disputDetails?.data,
  });

  // Open chat mutation
  const openChat = useMutation({
    mutationFn: async (chatId) => {
      const res = await apiClient.put(`/disputes/open-chat/${chatId}`, {});
      return res.data;
    },
    onSuccess: () => {
      console.log("✅ Chat opened successfully");
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
      // إعادة جلب الرسائل بعد فتح الدردشة
      setTimeout(() => {
        refetchChatMessages();
      }, 1000);
    },
    onError: (err) => {
      console.error("❌ Error opening chat:", err);
      showErrorAlert(err, "error");
    },
  });
  // Open support chat mutation
  const openSupportChat = useMutation({
    mutationFn: async (ticketId) => {
      if (!ticketId) return;

      const res = await apiClient.put(
        `/technical-support-tickets/${ticketId}/open-chat`,
        {},
      );

      return res.data;
    },

    onSuccess: () => {
      console.log("✅ Support chat opened successfully");

      queryClient.invalidateQueries({
        queryKey: ["supportById", supportId],
      });

      // Optional: refetch messages if you have chat for support
      // setTimeout(() => {
      //   refetchChatMessages();
      // }, 1000);
    },

    onError: (err) => {
      console.error("❌ Error opening support chat:", err);
      showErrorAlert(err, "error");
    },
  });
  const openSupportChatLoading = openSupportChat.isLoading;
  const openChatLoading = openChat.isLoading;

  // Close chat mutation
  const closeChat = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(`/disputes/close/${disputId}`, {});
      return res.data;
    },
    onSuccess: () => {
      console.log("✅ Chat closed successfully");
    },
    onError: (err) => {
      console.error("❌ Error closing chat:", err);
      showErrorAlert(err, "error");
    },
  });
  const closeSupportChat = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(`/disputes/close/${disputId}`, {});
      return res.data;
    },
    onSuccess: () => {
      console.log("✅ Chat closed successfully");
    },
    onError: (err) => {
      console.error("❌ Error closing chat:", err);
      showErrorAlert(err, "error");
    },
  });

  const closeChatLoading = closeChat.isLoading;
  const closeSupportChatLoading = closeSupportChat.isLoading;

  // Send message to chat - تم تحديثها لدعم الوسائط
  const sendChatMessage = async (chatId, data) => {
    try {
      let formData;

      // التحقق مما إذا كان data هو FormData أو نص عادي
      if (data instanceof FormData) {
        formData = data;
      } else {
        formData = new FormData();
        formData.append("message", data);
      }

      // إضافة fileType إذا كان هناك ملفات
      if (formData.has("media")) {
        // تحديد نوع الملف
        const file = formData.get("media");
        let fileType = "file";

        if (file instanceof File) {
          if (file.type.startsWith("image/")) {
            fileType = "image";
          } else if (file.type.includes("pdf")) {
            fileType = "pdf";
          } else if (
            file.type.includes("word") ||
            file.type.includes("document")
          ) {
            fileType = "document";
          } else if (
            file.type.includes("excel") ||
            fileType.includes("spreadsheet")
          ) {
            fileType = "spreadsheet";
          } else if (file.type.includes("video")) {
            fileType = "video";
          } else if (file.type.includes("audio")) {
            fileType = "audio";
          }
        }

        formData.append("fileType", fileType);
      }

      // 1. أرسل الرسالة عبر REST API
      // Realtime host مختلف عن VITE_API_URL، نستخدم axios مباشرة
      const token = sessionStorage.getItem("ecoToken");
      const apiResult = await axios.post(
        `${getRealtimeBaseUrl()}/Chat/${chatId}/messages`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
            "Accept-Language": getAcceptLanguage(),
          },
        },
      );

      console.log("✅ Message sent via API:", apiResult.data);

      const conn = connectionRef.current;

      if (
        isConnected &&
        conn &&
        conn.state === signalR.HubConnectionState.Connected
      ) {
        try {
          // إرسال إشعار عبر SignalR
          let messageText;

          if (data instanceof FormData) {
            const textMessage = formData.get("message");
            const fileCount = formData.getAll("media").length;

            messageText =
              textMessage ||
              (fileCount > 0 ? `Sent ${fileCount} file(s)` : "Message sent");
          } else {
            messageText = data;
          }

          await sendSignalRMessage(chatId, messageText);
        } catch (signalrError) {
          console.warn("⚠️ Could not send via SignalR:", signalrError);
        }
      }

      // 3. إعادة جلب الرسائل بعد إرسال الرسالة
      setTimeout(() => {
        refetchChatMessages();
      }, 500);

      return apiResult.data;
    } catch (error) {
      console.error("❌ Error sending message:", error);
      throw error;
    }
  };

  // close disput
  const closeDisput = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(`/disputes/close/${disputId}`, {});
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Dispute closed successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const closeDisputLoading = closeDisput.isLoading;
  const handleCloseDisput = () => {
    closeDisput.mutate();
  };

  // quick action
  const quickAction = useMutation({
    mutationFn: async () => {
      const filteredData = Object.fromEntries(
        Object.entries(quickData).filter(
          ([value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const res = await apiClient.put(
        `/disputes/${disputId}/admin-update`,
        filteredData,
      );
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Quick action updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
      setQuickAction({
        status: "",
        assigned_to: null,
        priority: "",
      });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const quickActionLoading = quickAction.isLoading;
  const handleQuickAction = () => {
    quickAction.mutate();
  };

  // escalate to admin
  const escalateToAdmin = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(`/disputes/approve/${disputId}`, {});
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Escalated successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const escalateToAdminLoading = escalateToAdmin.isLoading;
  const handleEscalateToAdmin = () => {
    escalateToAdmin.mutate();
  };

  // make Decision
  const makeDecision = useMutation({
    mutationFn: async (isFinal = false) => {
      const summary = decisionData?.decision_summary?.trim();
      const refundAmountRaw = decisionData?.refund_amount;
      const refundAmount =
        refundAmountRaw === null ||
        refundAmountRaw === undefined ||
        refundAmountRaw === ""
          ? null
          : Number(refundAmountRaw);

      const penaltyTarget = decisionData?.penalty_driver
        ? "driver"
        : decisionData?.penalty_passenger
          ? "passenger"
          : null;

      // Keep payload aligned with backend contract
      const dataToSend = {
        is_final: isFinal,
        decision_summary: summary || "",
        penalty_target: penaltyTarget,
        refund_amount: Number.isFinite(refundAmount) ? refundAmount : null,
      };

      const res = await apiClient.put(
        `/disputes/${disputId}/decision`,
        dataToSend,
      );
      return res.data;
    },
    onSuccess: (_, isFinal) => {
      Swal.fire({
        icon: "success",
        title: isFinal
          ? "Final decision saved successfully"
          : "Decision saved as draft",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
      setDecisionData({
        decision_summary: "",
        penalty_driver: false,
        penalty_passenger: false,
        refund_amount: null,
        is_final: false,
      });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });

  const makeDecisionLoading = makeDecision.isLoading;

  // Update handleMakeDecision to accept is_final parameter
  const handleMakeDecision = (isFinal = false) => {
    makeDecision.mutate(isFinal);
  };

  // support
  const { data, error, isError, isLoading } = useQuery({
    queryKey: ["supportList", page],
    queryFn: async () => {
      const res = await apiClient.get(
        `/technical-support-tickets?page=${page}`,
      );
      return res.data;
    },
  });

  // suppportById
  const {
    data: supportById,
    error: supportByIdError,
    isError: supportByIdIsError,
    isLoading: supportByIdLoading,
  } = useQuery({
    queryKey: ["supportById", supportId],
    queryFn: async () => {
      const res = await apiClient.get(
        `/technical-support-tickets/${supportId}`,
      );
      return res.data;
    },
  });

  // support quickaction
  const supportQuickAction = useMutation({
    mutationFn: async () => {
      if (supportId === undefined) return;
      const filteredData = Object.fromEntries(
        Object.entries(quickDataSupport).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const res = await apiClient.put(
        `/technical-support-tickets/${supportId}/admin-update`,
        filteredData,
      );
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Quick Action Updated successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["supportList"] });
      queryClient.invalidateQueries({ queryKey: ["supportById"] });
      setQuickDataSupport({
        status: "",
        assigned_to: null,
        priority: "",
        channel: "",
      });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const supportQuickActionLoading = supportQuickAction.isLoading;
  const handleQuickActionSupport = () => {
    supportQuickAction.mutate();
  };

  // add note
  const addNote = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post("/technical-support-responses", note);
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Note added successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["supportList"] });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const addNoteLoading = addNote.isLoading;
  const handleAddNote = () => {
    addNote.mutate();
  };
  // change admin
  const changeAdmin = useMutation({
    mutationFn: async () => {
      const res = await apiClient.put(`/disputes/changeAdmin/${disputId}`, {
        admin_id: adminId,
      });
      return res.data;
    },
    onSuccess: () => {
      Swal.fire({
        icon: "success",
        title: "Admin changed successfully",
        showConfirmButton: false,
        timer: 1500,
      });
      queryClient.invalidateQueries({ queryKey: ["disputById"] });
    },
    onError: (err) => {
      showErrorAlert(err, "error");
    },
  });
  const changeAdminLoading = changeAdmin.isLoading;
  const handleChangeAdmin = () => {
    changeAdmin.mutate();
  };
  return {
    // SignalR functions
    isConnected,
    isConnecting,
    connectToSignalR,
    disconnectFromSignalR,
    joinChatRoom,
    leaveChatRoom,
    sendSignalRMessage,

    // Chat messages data - الجديد
    chatMessagesData,
    chatMessagesLoading,
    chatMessagesError,
    refetchChatMessages,

    // الدالة المساعدة للحصول على معرف الدردشة الحالي
    getCurrentChatId,

    // Original functions
    distputTypes,
    distputTypesError,
    distputTypesIsError,
    distputTypesLoading,
    register,
    errors,
    control,
    reset,
    addUpdateDistputTypeLoading,
    handleAddUpdateDistputType,
    distputTypeById,
    deleteTypeLoading,
    handleDeleteType,
    supportTypes,
    supportTypesError,
    supportTypesIsError,
    supportTypesLoading,
    supportTypesPage,
    setSupportTypesPage,
    registerSupportType,
    supportTypeErrors,
    supportTypeControl,
    resetSupportType,
    addUpdateSupportTypeLoading,
    handleAddUpdateSupportType,
    supportTypeById,
    deleteSupportTypeLoading,
    handleDeleteSupportType,
    distputs,
    distputsError,
    distputsIsError,
    distputsLoading,
    rejectDistPutLoading,
    handleRejectDistput,
    approveDistPutLoading,
    handleApproveDistput,
    disputDetails,
    disputDetailsError,
    disputDetailsIsError,
    disputDetailsLoading,
    refetchDisputDetails,
    // Chat methods
    openChat: openChat.mutateAsync,
    openChatLoading,
    closeChat: closeChat.mutateAsync,
    closeChatLoading,
    closeSupportChat: closeSupportChat.mutateAsync,
    closeSupportChatLoading,
    sendChatMessage,
    closeDisputLoading,
    handleCloseDisput,
    quickActionLoading,
    handleQuickAction,
    escalateToAdminLoading,
    handleEscalateToAdmin,
    makeDecisionLoading,
    handleMakeDecision,
    data,
    error,
    isError,
    isLoading,
    supportById,
    supportByIdError,
    supportByIdIsError,
    supportByIdLoading,
    supportQuickActionLoading,
    handleQuickActionSupport,
    addNoteLoading,
    handleAddNote,
    changeAdminLoading,
    handleChangeAdmin,
    // Support chat
    openSupportChat: openSupportChat.mutateAsync,
    openSupportChatLoading,
  };
};

export default useSupport;
